import { createSlice } from '@reduxjs/toolkit'
import {
    CogIcon,
    GlobeAltIcon,
    HomeIcon,
    LifebuoyIcon,
    RectangleStackIcon,
    UserGroupIcon,
    BuildingStorefrontIcon,
    NewspaperIcon
} from '@heroicons/react/24/outline'
import { ImMagicWand, ImTarget } from 'react-icons/im'
import { GiCalendar, GiFlowerEmblem } from 'react-icons/gi'
import { BiBook } from "react-icons/bi";
import { Resource } from '../components/common/enum/Resource'
import { MdCamera } from 'react-icons/md'
import {doesFetchThruObitOrgPlaces, getSetting, isCorpAndFetchThru} from '../components/common/Helpers'

const getSsoLink = (organization) => {
    return `/me/sso/${organization.id}/website-cms`
}

const showArrangements = (currentUser, userOrganization, organization) => {
    return organization.settings.sympathy_plan_enabled
}

const showReports = (currentUser, userOrganization, organization) => {
    return currentUser.organization_report_access.length > 0
}

const showWebsiteManager = (currentUser, userOrganization, organization) => {
    return organization.duda_site_id != null
}

const showPartnerStore = (currentUser, userOrganization, organization) => {
    return organization.organization_id_parent == null
}

const showLeads = (currentUser, userOrganization, organization) => {
    return organization.settings.lead_generation
}

const showBookSales = (currentUser, userOrganization, organization) => {
    return organization?.country_id !== 'CA'
}

const showCorpFetchThruOptions = (currentUser, userOrganization, organization) => {
    return isCorpAndFetchThru(organization)
}

const showReportsWhenFetchThruDisabled = (currentUser, userOrganization, organization) => {
    return !doesFetchThruObitOrgPlaces(organization)
}

function UrnIcon({ className }) {
    return (
        <svg fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg" version="1.1" x="0px" y="0px"
            viewBox="0 0 512.484 512.484" width="512" height="512">
            <path
                d="M416.301,226.658l-7.168-74.951c-0.351-3.664-2.035-7.071-4.735-9.573l-61.33-56.841   c-8.876-8.226-12.793-20.681-10.222-32.505l4.014-18.457c1.661-7.639-2.823-15.273-10.305-17.542L285.587,4.366   c-19.197-5.821-39.492-5.821-58.689,0l-40.968,12.423c-7.481,2.269-11.966,9.903-10.305,17.542l4.014,18.457   c2.571,11.825-1.346,24.28-10.222,32.506l-61.33,56.841c-2.7,2.502-4.385,5.909-4.735,9.573l-7.168,74.951   c-2.042,21.344-0.468,42.72,4.688,63.582l52.864,210.891c1.673,6.673,7.67,11.353,14.55,11.353h175.912   c6.88,0,12.877-4.68,14.55-11.353l52.876-210.939C416.768,269.378,418.343,248.003,416.301,226.658z M380.567,168.136l2.52,26.348   h-253.69l2.52-26.348H380.567z M189.809,107.297c16.624-15.407,23.96-38.736,19.144-60.884l-1.072-4.932l27.722-8.406   c13.501-4.094,27.774-4.095,41.279,0l27.722,8.406l-1.072,4.932c-4.816,22.147,2.52,45.477,19.144,60.883l33.275,30.839H156.534   L189.809,107.297z M382.512,282.946l-50.019,199.539H179.99l-50.007-199.49c-4.33-17.52-5.654-35.513-3.937-53.479l0.481-5.031   h259.429l0.481,5.031C388.155,247.481,386.831,265.474,382.512,282.946z" />
        </svg>
    )
}

export const navigationSlice = createSlice({
    name: 'navigation',
    initialState: {
        organizationNavigation: [
            {
                name: 'Dashboard',
                href: '/dashboard',
                icon: HomeIcon,
                count: organization => getSetting(organization, 'include_pending_guestbook_posts_count') ? organization.unapproved_obituary_post_count : null,
                resource: Resource.CurrentOrganization_Dashboard.Value
            },
            {
                name: 'Obituaries',
                href: '/obituaries',
                icon: RectangleStackIcon,
                count: organization => organization.needs_review_obituary_count,
                countHref: '/obituaries/page/1?needs_review=1',
                resource: Resource.Obituaries.Value
            },
            {
                name: 'Audience',
                href: '/audience',
                icon: ImTarget,
                count: organization => organization.unread_form_submissions_count,
                resource: Resource.CurrentOrganization_Audience.Value
            },
            {
                name: 'Website Content',
                href: '/website-content',
                icon: GlobeAltIcon,
                resource: Resource.CurrentOrganization_WebsiteContent.Value
            },
            { name: 'Users', href: '/users', icon: UserGroupIcon, resource: Resource.CurrentOrganization_Users.Value },
            { name: 'Events', href: '/events', icon: GiCalendar, resource: Resource.CurrentOrganization_Events.Value },
            {
                name: 'Reports',
                href: '/reports',
                icon: GiFlowerEmblem,
                resource: Resource.CurrentOrganization_Reports.Value,
                show: showReportsWhenFetchThruDisabled
            },
            {
                name: 'Lead Engine',
                href: '/lead-engine',
                icon: MdCamera,
                resource: Resource.CurrentOrganization_LeadGeneration.Value,
                show: showLeads
            },
            {
                name: 'Proofs',
                href: '/proofs',
                icon: BiBook,
                count: organization => organization.pending_proofs_count,
                resource: Resource.CurrentOrganization_Products_Proofs.Value,
                show: showBookSales,
            },
            { name: 'Settings', href: '/settings', icon: CogIcon, resource: Resource.CurrentOrganization_Settings.Value },
        ],
        adminNavigation: [
            { name: 'Dashboard', href: '/welcome' },
            { name: 'Reports', href: '/reports', icon: GiFlowerEmblem, show: showReports },
        ],
        corporationNavigation: [
            { name: 'Organizations', href: '/organizations', resource: Resource.Organizations_Index.Value },
            { name: 'Users', href: '/users', resource: Resource.Corporations_Users_Index.Value },
            { name: 'Obituaries', href: '/obituaries', resource: Resource.Obituaries.Value, show: showCorpFetchThruOptions },
            { name: 'Locations', href: '/locations', resource: Resource.CurrentOrganization_Locations.Value, show: showCorpFetchThruOptions },
            { name: 'Guestbook', href: '/guestbook', resource: Resource.CurrentOrganization_Dashboard_GuestbookApprovals.Value, show: showCorpFetchThruOptions },
            { name: 'Groups', href: '/groups', resource: Resource.Corporations_Groups.Value },
            { name: 'Reports', href: '/reports', resource: Resource.Corporations_Reports.Value, show: showReportsWhenFetchThruDisabled },
            { name: 'Proofs', href: '/proofs', resource: Resource.Corporations_Products_Proofs.Value },
            { name: 'Settings', href: '/settings', resource: Resource.Corporations_Settings_Standard.Value },
            {
                name: "What's New",
                href: '/releases',
                icon: NewspaperIcon,
                resource: Resource.WhatsNew.Value,
                count: user => user.unread_release_count
            },
        ],
        superAdminNavigation: [
            { name: 'Organizations', href: '/admin/organizations', resource: Resource.Organizations_Index.Value },
            { name: 'Users', href: '/admin/users', resource: Resource.Users.Value },
            { name: 'Reports', href: '/admin/reports', resource: Resource.Dashboard.Value },
            { name: 'Settings', href: '/admin/settings', resource: Resource.Settings.Value },
            { name: 'Audit', href: '/admin/audit', resource: Resource.Audit.Value },
            { name: 'Tickets', href: '/admin/tickets', resource: Resource.Tickets.Value },
            { name: 'Proofs', href: '/admin/proofs', resource: Resource.Products_Proofs.Value },
            {
                name: "What's New",
                href: '/admin/releases',
                icon: NewspaperIcon,
                resource: Resource.WhatsNew.Value,
                count: user => user.unread_release_count
            },
        ],
        userNavigation: [
            { name: 'Your Profile', href: '/profile' },
            { name: 'Documentation', href: '/developer', resource: Resource.Documentation.Value },
            {
                name: 'Log Out', href: '/logout', onClick: (e) => {
                    e.preventDefault();
                    window.location.href = "/logout"
                }
            }
        ],
        externalNavigation: [
            {
                name: 'Website Manager',
                icon: ImMagicWand,
                href: getSsoLink,
                resource: Resource.CurrentOrganization_Links_WebsiteManager.Value,
                show: showWebsiteManager
            },
            { name: 'Arrangements', icon: UrnIcon, href: 'https://www.sympathyplan.com', show: showArrangements },
            {
                name: 'Partner Store',
                icon: BuildingStorefrontIcon,
                href: 'https://www.tukiospartners.com',
                resource: Resource.CurrentOrganization_Links_PartnerStore.Value,
                show: showPartnerStore
            },
            { name: 'Support', icon: LifebuoyIcon, href: 'https://help.tukios.com/help/websites' },
        ],
        currentPage: 1,
        searchQuery: '',
        showDeleted: false,
        systemError: false,
        currentStatus: null,
        currentType: null,
        redirect: false
    },
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = parseInt(action.payload)
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload
            state.currentPage = 1
        },
        setShowDeleted: (state, action) => {
            state.showDeleted = action.payload
        },
        setCurrentStatus: (state, action) => {
            state.currentStatus = action.payload
        },
        setCurrentType: (state, action) => {
            state.currentType = action.payload
        },
        setSystemError: (state, action) => {
            state.systemError = action.payload
        },
        setRedirect: (state, action) => {
            state.redirect = action.payload
        }
    }
})

export const {
    setCurrentPage,
    setSearchQuery,
    setShowDeleted,
    setSystemError,
    setCurrentStatus,
    setCurrentType,
    setRedirect
} = navigationSlice.actions
export default navigationSlice.reducer
